<template>
  <div>
    <div
      v-show="showConfirmEmail"
      class="w-full"
    >
      <vetster-notice color="yellow">
        <h2 class="text-neutral-900 font-semibold">
          {{ $t('login.did-you-mistype-your-email') }}
        </h2>
        <p class="mt-2 text-neutral-700">
          <i18n
            path="login.it-looks-like-you-made-a-typo-in-email-did-you-mean"
            tag="span"
          >
            <template #email>
              <a
                href="#"
                class="font-bold text-ocean-700"
                @click.prevent="inputs.email = suggestEmail"
              >
                {{ suggestEmail }}
              </a>
            </template>
          </i18n>
        </p>
      </vetster-notice>

      <div class="mt-4">
        <form @submit.prevent="confirmEmailAndSubmit">
          <div>
            <vetster-input
              v-model.trim="v$.inputs.email.$model"
              :label="$t('login.confirm-your-email')"
              type="email"
              autocomplete="email"
              :disabled="submitting"
              :shake="shouldShake('email', v$.inputs.email.$error)"
              :error="hasError('email', v$.inputs.email.$error)"
              :feedback="getErrorMessage('email', v$.inputs.email.$error, $t('login.errors.please-enter-email'))"
              is-rebranded
            />
          </div>
          <div class="mt-6 flex flex-col">
            <span class="block w-full rounded-md shadow-sm">
              <vetster-button
                :disabled="submitting"
                class="w-full flex justify-center"
                type="submit"
                variant="ocean"
                size="lg"
              >
                <span>{{ $t('login.confirm-email') }}</span>
                <fa
                  v-if="submitting"
                  :icon="['fal', 'circle-notch']"
                  class="text-xl text-white-500 ml-2 fa-spin"
                />
              </vetster-button>
            </span>
          </div>
        </form>
      </div>
    </div>
    <div
      v-show="!showConfirmEmail"
      class="grid justify-items-center space-y-4"
    >
      <div v-if="isSocialite">
        <vetster-notice color="ocean">
          <p class="text-neutral-700">
            {{ $t('login.confirm-details') }}
          </p>
        </vetster-notice>
      </div>

      <div class="w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:space-x-4 lg:justify-between">
        <vetster-input
          v-model.trim="v$.inputs.first_name.$model"
          name="first_name"
          :label="$t('common.first-name')"
          autocomplete="off"
          :disabled="submitting"
          :shake="shouldShake('first_name', v$.inputs.first_name.$error)"
          :error="hasError('first_name', v$.inputs.first_name.$error)"
          :feedback="getErrorMessage('first_name', v$.inputs.first_name.$error, $t('login.errors.please-enter-your-first-name'))"
          is-rebranded
        />
        <vetster-input
          v-model.trim="v$.inputs.last_name.$model"
          name="last_name"
          :label="$t('common.last-name')"
          autocomplete="off"
          :disabled="submitting"
          :shake="shouldShake('last_name', v$.inputs.last_name.$error)"
          :error="hasError('last_name', v$.inputs.last_name.$error)"
          :feedback="getErrorMessage('last_name', v$.inputs.last_name.$error, $t('login.errors.please-enter-your-last-name'))"
          is-rebranded
        />
      </div>
      <div class="space-y-4 w-full">
        <vetster-input
          v-model.trim="v$.inputs.email.$model"
          name="email"
          :label="$t('common.email')"
          autocomplete="off"
          :disabled="submitting"
          :shake="shouldShake('email', v$.inputs.email.$error)"
          :error="hasError('email', v$.inputs.email.$error)"
          :feedback="getErrorMessage('email', v$.inputs.email.$error, $t('login.errors.please-enter-email'))"
          is-rebranded
        />
        <template v-if="!isSocialite">
          <vetster-input
            v-model.trim="v$.inputs.password.$model"
            :label="$t('common.password')"
            name="password"
            type="password"
            autocomplete="current-password"
            :lazy-update="false"
            is-rebranded
            :disabled="submitting"
            :shake="shouldShake('password', v$.inputs.password.$error)"
            :error="hasError('password', v$.inputs.password.$error)"
          />
          <vetster-password-strength
            v-if="v$.inputs.password.$dirty"
            :password="inputs.password"
          />
        </template>
      </div>
      <div class="grid lg:grid-cols-10 w-full lg:space-x-4">
        <vetster-locale-changer
          :is-page-reload="false"
          :is-partner="isPartner"
          :is-read-only="isLocaleReadOnly"
          is-rebranded
          class="mt-1 lg:col-span-5"
        />
        <div class="mt-4 lg:my-auto lg:col-span-5">
          <div v-if="!isPartner">
            <div v-show="promoCodeInputVisible">
              <vetster-input
                v-model.trim="v$.inputs.promo_code.$model"
                :label="promoCodeLabel || $t('login.registration-code')"
                name="promo_code"
                type="text"
                :optional="!isPromoCodeRequired"
                :shake="shouldShake('promo_code', hasPromoCodeError)"
                :error="hasError('promo_code', hasPromoCodeError)"
                :feedback="getErrorMessage('promo_code', hasPromoCodeError, $t('login.errors.please-enter-promo-code'))"
                :maxlength="50"
                :disabled="isPromoCodeReadonly"
                is-rebranded
                class="flex-1"
              />
            </div>
            <div
              v-show="!promoCodeInputVisible"
              class="flex items-center lg:justify-end"
            >
              <a
                data-testid="have-promo-code"
                class="flex cursor-pointer items-center justify-start text-ocean-950 text-xs font-light"
                :class="linkVariant"
                @click.prevent="promoCodeInputVisible = true"
                @keydown.prevent="promoCodeInputVisible = true"
              >
                <fa
                  :icon="['fal', 'tag']"
                  class="text-ocean-500 text-base mr-2"
                />
                <span class="underline">{{ $t('common.have-a-code') }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="space-y-4 w-full">
        <div
          v-if="showTermsOfUseCheckbox"
          class="relative flex items-center flex-row"
        >
          <div class="flex items-center h-5">
            <input
              id="terms"
              v-model.trim="v$.inputs.terms.$model"
              name="terms"
              :aria-label="$t('login.i-have-read-and-agree-to-vetster')"
              type="checkbox"
              class="focus:ring-ocean-500 h-4 w-4 text-ocean-600 border-neutral-300 rounded"
            />
          </div>
          <div class="ml-3 text-xs">
            <label
              for="terms"
              data-testid="want-marketing-messages"
              class="text-neutral-700"
              v-html="$t('login.i-have-read-and-agree-to-vetster', legalUrls)"
            />
          </div>
        </div>
        <div
          v-if="showConsentControl"
          class="relative flex items-center flex-row"
        >
          <div class="flex items-center h-5">
            <input
              id="marketing_messages"
              v-model.trim="v$.inputs.marketing_messages.$model"
              name="marketing_messages"
              :aria-label="$t('login.i-want-marketing-messages')"
              type="checkbox"
              class="focus:ring-ocean-500 h-4 w-4 text-ocean-600 border-neutral-300 rounded"
            />
          </div>
          <div class="ml-3 text-xs">
            <label
              for="marketing_messages"
              data-testid="want-marketing-messages"
              class="text-neutral-700"
            >
              {{ $t('login.i-want-marketing-messages') }}
            </label>
          </div>
        </div>
      </div>
      <div class="w-full">
        <vetster-button
          id="register-btn"
          type="button"
          variant="ocean"
          size="lg"
          :disabled="submitting"
          :is-loading="submitting"
          class="w-full"
          @click="confirmEmailAndSubmit"
        >
          {{ $t('login.agree-and-continue') }}
        </vetster-button>
      </div>
      <div
        v-if="showTermsOfUseText"
        class="w-full"
      >
        <p
          data-testid="marketing-emails-description"
          class="text-xs text-neutral-700"
        >
          {{ $t('login.by-selecting-agree') }}
          <span v-html="$t('login.i-agree-to-vetster', legalUrls)" />
        </p>
      </div>

      <div
        v-if="showMarketingText"
        class="w-full"
      >
        <div
          data-testid="marketing-emails-description"
          class="text-xs text-neutral-700"
        >
          {{ $t('login.vetster-sends-marketing-messages-to-pet-parents') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import {
  email, required, requiredIf,
} from '@vuelidate/validators';
import isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters } from 'vuex';

import VetsterNotice from '@/brand-components/VetsterNotice';
import VetsterPasswordStrength from '@/brand-components/VetsterPasswordStrength';
import VetsterLocaleChanger from '@/components/VetsterLocaleChanger';
import { passwordStrength } from '@/config';
import UserType from '@/enums/UserType';
import { titleCase } from '@/helpers/format';
import {
  getQueryParams,
  isSPA,
} from '@/helpers/url';
import { i18n } from '@/i18n';
import HasAuthForm from '@/mixins/HasAuthForm';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import ParseErrors from '@/mixins/ParseErrors';

export default {
  name: 'VetsterAuthRegister',

  components: {
    VetsterLocaleChanger,
    VetsterNotice,
    VetsterPasswordStrength,
  },

  mixins: [
    HasAuthForm,
    LocalizesRoutes,
    ParseErrors,
  ],

  props: {
    email: {
      type: String,
      default: '',
    },
    globalLocale: {
      type: String,
      default: '',
    },
    hideMarketingEmails: {
      default: false,
      type: Boolean,
    },
    isBooking: {
      type: Boolean,
      default: false,
    },
    isLocaleReadOnly: {
      type: Boolean,
      default: false,
    },
    isPartner: {
      default: false,
      type: Boolean,
    },
    isPromoCodeRequired: {
      type: Boolean,
      default: false,
    },
    isPromoCodeReadonly: {
      default: false,
      type: Boolean,
    },
    isSocialite: {
      type: Boolean,
      default: false,
    },
    promoCode: {
      type: String,
      default: null,
    },
    promoCodeLabel: {
      default: null,
      type: String,
    },
  },

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      suggestEmail: '',
      showConfirmEmail: false,
      promoCodeFailed: false,
      promoCodeInputVisible: false,
      inputs: {
        first_name: '',
        last_name: '',
        email: '',
        password: null,
        promo_code: null,
        marketing_messages: true,
        terms: true,
      },
    };
  },

  computed: {
    ...mapGetters(['getUser']),

    ...mapGetters('socialite', [
      'getRegisterWithSocialite',
      'isOpenForRegistration',
      'getRegistrationSource',
      'hasProfilePhoto',
    ]),

    getRegisterUserPayload() {
      return {
        email: this.inputs.email.toLowerCase(),
        password: this.inputs.password,
        first_name: titleCase(this.inputs.first_name),
        last_name: titleCase(this.inputs.last_name),
        type: this.inputs.is_partner ? UserType.Partner : UserType.Client,
        platform: 'WEB',
        promo_code: this.inputs.promo_code,
        referral_code: this.inputs.referral_code,
        locale: this.getLocale,
        locale_override: this.hasGlobalLocale ? this.globalLocale : null,
        source: this.getRegistrationSource,
        suggestion_seen: this.inputs.suggestion_seen,
        marketing_emails: this.inputs.marketing_messages,
        marketing_sms: this.inputs.marketing_messages,
        provider_type: this.getRegisterWithSocialite?.provider_type,
        access_token: this.getRegisterWithSocialite?.access_token,
      };
    },

    submitting() {
      return this.loading || this.validating;
    },

    hasGlobalLocale() {
      return !isEmpty(this.globalLocale);
    },

    getLocale() {
      return i18n.locale;
    },

    legalUrls() {
      return {
        privacyUrl: this.legalUrl('legal/privacy'),
        termsUrl: this.legalUrl('legal/terms'),
      };
    },

    showConsentControl() {
      let flag;

      switch (this.getLocale) {
        case 'en-CA':
        case 'fr-CA':
          flag = 'auth_reg_explicit_consent_ca';
          break;
        case 'en-US':
          flag = 'auth_reg_explicit_consent_us';
          break;
        default:
          return true;
      }

      return this.$statsig.ready && this.$statsig.checkGate(flag);
    },

    hasPromoCodeError() {
      return this.promoCodeFailed || this.v$.inputs.promo_code.$error;
    },

    redirectUrl() {
      if (this.isPromoCodeReadonly && this.promoCode && !this.isBooking) {
        return this.guidedSearchUrl;
      }

      if (!this.isBooking) {
        return this.cxWelcomeUrl;
      }

      if (this.redirectToDashboard) {
        return this.dashboardUrl;
      }

      return null;
    },

    // Controls visibility of the implicit consent copy when explicit consent is not required
    showImpliedConsent() {
      return !this.showExplicitConsent || this.hideMarketingEmails;
    },

    // Controls visibility of the explicit consent checkboxes (opt in)
    showExplicitConsent() {
      return this.showConsentControl && !this.hideMarketingEmails && this.getLocale === 'en-GB';
    },

    // Controls visibility of the opt-out checkboxes when explicit consent is shown
    showOptOut() {
      return this.showConsentControl && !this.hideMarketingEmails && this.showImpliedConsent;
    },

    // Controls visibility of the marketing text below the signup
    showMarketingText() {
      return !this.showOptOut && !this.showExplicitConsent && !this.isPartner;
    },

    // Controls visibility of the terms of use and privacy policy checkbox
    showTermsOfUseCheckbox() {
      return this.getLocale === 'en-GB';
    },

    // Controls visibility of the terms of use and privacy policy text copy
    showTermsOfUseText() {
      return this.getLocale !== 'en-GB';
    },
  },

  created() {
    this.inputs.email = this.email;
    this.inputs.is_partner = this.isPartner;
    this.$analytics.trackGA({ eventName: 'gx:identity:register:load' });

    const { redirect, code, promo } = getQueryParams();

    if (code) {
      this.inputs.promo_code = code;
    } else if (promo) {
      this.inputs.promo_code = promo;
    } else if (this.promoCode) {
      this.inputs.promo_code = this.promoCode;
    } else {
      this.inputs.promo_code = this.$cookies.get('v-utm-promo');
    }

    if (this.inputs.promo_code) {
      this.promoCodeInputVisible = true;
    }

    if (redirect) {
      this.$store.commit('setRedirect', redirect);
    }

    this.clearErrors();
  },

  validations() {
    const validation = {
      inputs: {
        first_name: {
          required,
        },
        last_name: {
          required,
        },
        email: {
          required,
          email,
        },
        password: {},
        terms: {},
        marketing_messages: {},
        promo_code: {
          required: requiredIf(() => this.isPromoCodeRequired),
        },
      },
    };

    if (!this.isSocialite) {
      validation.inputs.password = {
        required,
        passwordStrength,
      };
    }

    if (this.showExplicitConsent) {
      validation.inputs.terms = {
        checked: (value) => value === true,
      };
    }

    return validation;
  },

  mounted() {
    if (this.getLocale === 'en-GB') {
      this.inputs.marketing_messages = false;
      this.inputs.terms = false;
    }
    if (this.isSocialite) {
      this.setupSocialiteInfo();
    }
  },

  methods: {
    ...mapActions(['checkAuthAndUser']),
    promoCodeRedemptionUrl() {
      return `${import.meta.env.VITE_API_URL}p/redemption-code/`
        + `${encodeURIComponent(this.inputs.promo_code)}/`
        + `${encodeURIComponent(this.inputs.email)}`;
    },
    async confirmEmailAndSubmit() {
      this.inputs.suggestion_seen = 1;
      await this.registerUser();
    },
    async validatePromoCode() {
      this.promoCodeFailed = false;
      if (this.inputs.promo_code) {
        this.loading = true;

        try {
          await this.$api.get(this.promoCodeRedemptionUrl());
        } catch (e) {
          this.promoCodeFailed = true;
          this.parseError(e, true);
          this.loading = false;
          this.validating = false;

          return false;
        }
      }

      return true;
    },
    resetForm() {
      this.clearErrors();
      this.v$.inputs.$reset();
      this.v$.inputs.$touch();
      this.resetEmailConflict();
    },
    register() {
      return this.isSocialite
        ? this.$auth.registerViaSocialiteV2(this.getRegisterUserPayload)
        : this.$auth.registerUser(this.getRegisterUserPayload);
    },
    async registerUser() {
      this.validating = true;
      this.resetForm();

      this.$analytics.trackGA({ eventName: 'gx:identity:register:submit-attempt' });

      if (!await this.validatePromoCode()) {
        return;
      }

      const referralCode = this.$cookies.get('v-raf');
      if (referralCode) {
        this.inputs.referral_code = referralCode;
      }

      if (this.v$.inputs.$invalid) {
        this.shakeErrors = true;
        this.removeShake();
        this.$analytics.trackRegisterFailure(this.getRegisterWithSocialite?.provider_type);
        this.loading = false;
        this.validating = false;

        return;
      }

      this.loading = true;
      this.validating = false;

      this.register().then(
        async () => {
          await this.$store.dispatch('fetchUser');

          await this.trackRegister();

          this.$toast(this.$t('login.welcome-to-vetster'));

          if (await this.$auth.authenticate()) {
            if (this.hasProfilePhoto) {
              this.$store.dispatch('socialite/storeProfilePhoto', {
                profile_path: this.getRegisterWithSocialite.profile_photo,
              });
            }
            await this.checkAuthAndUser();
            this.$emit('action-completed', 'register');

            const { redirectUrl } = this;

            if (redirectUrl) {
              this.$nextTick(() => {
                window.location.href = redirectUrl;
              });
            } else if (!isSPA(window.location.pathname)) {
              this.$nextTick(() => {
                window.location.reload();
              });
            }
          }
        },
        (error) => {
          const suggestEmail = error.response?.data?.data?.email_suggestion?.[0];
          if (suggestEmail) {
            this.loading = false;
            this.validating = false;
            this.showConfirmEmail = true;
            this.suggestEmail = suggestEmail;
          } else {
            this.parseError(error);
          }
        },
      )
        .finally(() => {
          this.loading = false;
        });
    },
    async trackRegister() {
      // ------------------------------
      // Set Global Variable for GA Enhanced Conversion to pickup from GTM
      // https://support.google.com/google-ads/answer/10172785?hl=en&ref_topic=3165803#Javascript_GTM&zippy=%2Cenable-enhanced-conversions-in-google-tag-manager-tag%2Cenable-enhanced-conversions-in-google-tag-manager-tag-and-create-custom-javascript-variable
      // These items will be hashed using SHA-256 before being sent to Google.
      // ------------------------------
      window.enhancedConversion = {
        email: this.getUser.email || null,
      };
      // ------------------------------
      this.$analytics.trackRegister(this.getRegisterWithSocialite?.provider_type);
      this.$analytics.trackSignIn(this.getRegisterWithSocialite?.provider_type);
    },
    setupSocialiteInfo() {
      this.inputs.first_name = this.getRegisterWithSocialite.first_name;
      this.inputs.last_name = this.getRegisterWithSocialite.last_name;
      this.inputs.email = this.getRegisterWithSocialite.email;
    },
  },
};
</script>
